<template>
  <div class="th-page">
    <header class="th-page__hero">
      <div class="th-page__hero-text">
        <h1 class="th-page__title">Түүх</h1>
        <p class="th-page__subtitle">Олгосон / цуцлагдсан трак кодууд</p>
      </div>
      <div v-if="totalRecords" class="th-page__count">
        <span class="th-page__count-num">{{ totalRecords }}</span>
        <span class="th-page__count-label">бүртгэл</span>
      </div>
    </header>

    <div class="th-page__toolbar">
      <div class="th-page__search">
        <el-input
          v-if="checkPermissionsPage('app_list_search_mongolia')"
          v-model="searchQuery"
          placeholder="Утас эсвэл track code..."
          size="medium"
          clearable
          prefix-icon="el-icon-search"
          @clear="clearSearch"
          @keyup.enter.native="searchItem"
        />
        <el-input
          v-else-if="checkPermissionsPage('app_list_search_china')"
          v-model="searchQuery"
          placeholder="Трак код оруулна уу"
          size="medium"
          clearable
          prefix-icon="el-icon-search"
          @clear="clearSearch"
          @keyup.enter.native="searchItem"
        />
        <el-button type="primary" size="medium" icon="el-icon-search" @click="searchItem">
          Хайх
        </el-button>
      </div>
    </div>

    <el-empty
      v-if="!items.length && !$root.fullscreenLoading"
      class="th-page__empty"
      description="Түүх олдсонгүй"
      :image-size="80"
    />

    <div v-else class="th-card-list">
      <el-card
        v-for="(item, index) in items"
        :key="item.track_code || item.id || index"
        class="th-card"
        shadow="hover"
        :body-style="{ padding: '0' }"
      >
        <div
          class="th-card__accent"
          :class="'th-card__accent--' + statusAccentClass(item.progress_code)"
        />
        <div class="th-card__body">
          <div class="th-card__head">
            <span class="th-card__code">{{ item.track_code }}</span>
            <el-tag
              v-if="item.progress_code"
              :type="statusTagType(item.progress_code)"
              size="small"
              effect="dark"
            >
              {{ returnStatusValue(item.progress_code) }}
            </el-tag>
          </div>
          <div class="th-card__meta" @click="openProgressDialog('one', item)">
            <span v-if="item.progress_created_at" class="th-card__date">
              {{ item.progress_created_at.split('T')[0] }}
            </span>
            <dl class="th-card__dl">
              <div
                v-if="item.phone_number && (checkPermissionsPage('admin') || checkPermissionsPage('mongolia_branch'))"
                class="th-card__row"
              >
                <dt>Утас</dt>
                <dd>
                  <a :href="'tel:' + item.phone_number" class="th-card__phone" @click.stop>
                    {{ item.phone_number }}
                  </a>
                </dd>
              </div>
              <div
                v-if="item.taviur_code && (checkPermissionsPage('admin') || checkPermissionsPage('mongolia_branch'))"
                class="th-card__row"
              >
                <dt>Тавиур</dt>
                <dd>{{ item.taviur_code }}</dd>
              </div>
              <div
                v-if="item.price && (checkPermissionsPage('admin') || checkPermissionsPage('mongolia_branch'))"
                class="th-card__row"
              >
                <dt>Үнэ</dt>
                <dd>{{ item.price | formatCurrency }}</dd>
              </div>
              <div
                v-if="item.irsen_salbariin_ner && (checkPermissionsPage('admin') || checkPermissionsPage('mongolia_branch'))"
                class="th-card__row"
              >
                <dt>Салбар</dt>
                <dd>{{ item.irsen_salbariin_ner }}</dd>
              </div>
              <div v-if="item.jin" class="th-card__row">
                <dt>Жин</dt>
                <dd>{{ item.jin }}гр</dd>
              </div>
              <div v-if="item.config_type_name" class="th-card__row">
                <dt>Төрөл</dt>
                <dd>{{ item.config_type_name }}</dd>
              </div>
            </dl>
          </div>
          <div class="th-card__footer">
            <el-button
              class="th-card__btn"
              size="small"
              type="primary"
              plain
              icon="el-icon-view"
              @click="openProgressDetailDialog(item.id)"
            >
              Дэлгэрэнгүй
            </el-button>
          </div>
        </div>
      </el-card>
    </div>

    <div v-if="items.length" class="th-page__scroll-status">
      <div v-if="loadingMore" class="th-page__loading-more">
        <i class="el-icon-loading" /> Уншиж байна...
      </div>
      <div v-else-if="finished" class="th-page__end">
        Нийт {{ totalRecords }} · бүгдийг харуулсан
      </div>
      <div v-else class="th-page__hint">
        {{ items.length }} / {{ totalRecords }} · доош гүйлгэж үргэлжлүүлнэ
      </div>
    </div>
    <div ref="scrollSentinel" class="th-page__sentinel" aria-hidden="true" />

    <transition name="th-fade">
      <div v-if="loadingMore" class="th-scroll-loader" aria-live="polite">
        <div class="th-scroll-loader__inner">
          <i class="el-icon-loading" />
          <span>Дараагийн хуудас ачаалж байна...</span>
        </div>
      </div>
    </transition>

    <ProgressDetailDialog
      v-if="$root.progressDetailDialog"
      :progressId="progress_id"
      @refreshData="resetAndFetch"
    />
  </div>
</template>

<script>
import services from '@/helpers/services'
import { checkPermissions } from '@/helpers/custom'
import ProgressDetailDialog from '@/views/Dialog/ProgressDetailDialog.vue'

export default {
  name: 'TrackCodeHistory',
  components: {
    ProgressDetailDialog
  },
  props: {
    progressCode: {
      type: Array,
      default: () => []
    },
    isPaid: {
      type: Boolean
    }
  },
  data () {
    return {
      currentPage: 1,
      pageSize: 20,
      totalRecords: 0,
      loadingMore: false,
      finished: false,
      items: [],
      searchQuery: null,
      formData: {
        type: null,
        selectedTrackCodes: [],
        is_paid: false,
        paid_type: 'transaction',
        totalPrice: null,
        countTrackCode: 0,
        taviur_codes: [],
        deliveryAmount: 0,
        description: null,
        jin: 0,
        user_status: null,
        phone_number: [],
        save_price: 0
      },
      transacProgressCode: '200,190',
      progress_id: null,
      scrollObserver: null
    }
  },
  created () {
    this.resetAndFetch()
  },
  mounted () {
    this.setupScrollObserver()
  },
  beforeDestroy () {
    this.teardownScrollObserver()
  },
  methods: {
    setupScrollObserver () {
      this.teardownScrollObserver()
      if (typeof IntersectionObserver === 'undefined') {
        window.addEventListener('scroll', this.onWindowScroll, { passive: true })
        return
      }
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry && entry.isIntersecting) this.loadMore()
        },
        { root: null, rootMargin: '240px', threshold: 0 }
      )
      this.$nextTick(() => {
        if (this.$refs.scrollSentinel) {
          this.scrollObserver.observe(this.$refs.scrollSentinel)
        }
      })
    },
    teardownScrollObserver () {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect()
        this.scrollObserver = null
      }
      window.removeEventListener('scroll', this.onWindowScroll)
    },
    onWindowScroll () {
      const nearBottom =
        window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 280
      if (nearBottom) this.loadMore()
    },
    observeSentinel () {
      this.$nextTick(() => {
        if (this.scrollObserver && this.$refs.scrollSentinel) {
          this.scrollObserver.disconnect()
          this.scrollObserver.observe(this.$refs.scrollSentinel)
        }
      })
    },
    resetAndFetch () {
      this.currentPage = 1
      this.finished = false
      this.items = []
      this.fetchItems({ append: false })
    },
    loadMore () {
      if (this.finished || this.loadingMore || this.$root.fullscreenLoading) return
      if (this.items.length >= this.totalRecords && this.totalRecords > 0) {
        this.finished = true
        return
      }
      this.currentPage += 1
      this.fetchItems({ append: true })
    },
    openProgressDetailDialog (data) {
      this.progress_id = data
      this.$root.progressDetailDialog = true
    },
    openProgressDialog (data, item) {
      if (data === 'one') {
        this.formData.selectedTrackCodes = [item.track_code]
        this.formData.is_paid = item.is_paid
        this.formData.paid_type = item.paid_type
        this.formData.totalPrice = item.price
        this.formData.taviur_codes = [item.taviur_code]
        this.formData.deliveryAmount = item.delivery_amount
        this.formData.description = item.description
        this.formData.jin = item.jin
        this.formData.user_status = item.status
        this.formData.save_price = item.save_price
        this.formData.type = data
      }
    },
    resolveSelectedComponent () {
      if (this.transacProgressCode) {
        return Array.isArray(this.transacProgressCode)
          ? this.transacProgressCode.join(',')
          : this.transacProgressCode
      }
      if (Array.isArray(this.progressCode) && this.progressCode.length) {
        return this.progressCode.join(',')
      }
      return this.progressCode
    },
    async fetchItems ({ append = false } = {}) {
      const selectedComponent = this.resolveSelectedComponent()
      if (!selectedComponent) return

      if (append) {
        this.loadingMore = true
      } else {
        this.$root.fullscreenLoading = true
        this.currentPage = 1
        this.finished = false
      }

      try {
        const response = await services.getTrackCodesHistory({
          selectedComponent,
          page: this.currentPage,
          limit: this.pageSize,
          searchQuery: this.searchQuery
        })
        const rows = Array.isArray(response.data) ? response.data : []
        this.totalRecords = response.total || 0

        if (append) {
          const existing = new Set(this.items.map(i => i.track_code || i.id))
          const fresh = rows.filter(r => !existing.has(r.track_code || r.id))
          this.items = this.items.concat(fresh)
        } else {
          this.items = rows
        }

        const totalPages = response.totalPages || 0
        if (
          !rows.length ||
          this.items.length >= this.totalRecords ||
          (totalPages && this.currentPage >= totalPages)
        ) {
          this.finished = true
        } else {
          this.finished = false
        }
        this.observeSentinel()
      } catch (error) {
        console.error('Алдаа:', error)
        if (append) this.currentPage = Math.max(1, this.currentPage - 1)
        if (!append) {
          this.items = []
          this.totalRecords = 0
          this.finished = true
        }
        this.$notify({
          title: 'Алдаа',
          message: error && error.message ? error.message : String(error),
          type: 'error'
        })
      } finally {
        this.loadingMore = false
        this.$root.fullscreenLoading = false
      }
    },
    async searchItem () {
      this.resetAndFetch()
    },
    clearSearch () {
      this.searchQuery = null
      this.resetAndFetch()
    },
    returnStatusValue (data) {
      const result = (this.$root.statusArray || []).find(element => element.value === data)
      return result ? result.label : 'empty'
    },
    statusTagType (status) {
      const map = { 200: 'success', 190: 'danger', 180: 'danger', 130: 'warning' }
      return map[status] !== undefined ? map[status] : 'info'
    },
    statusAccentClass (status) {
      const map = { 200: 'success', 190: 'danger', 180: 'danger', 130: 'warning' }
      return map[status] || 'muted'
    },
    checkPermissionsPage (data) {
      return checkPermissions(data)
    }
  }
}
</script>

<style scoped>
.th-page {
  max-width: 640px;
  margin: 0 auto;
  padding: 16px 14px 28px;
  background: linear-gradient(180deg, #f1f5f9 0%, #f8fafc 40%, #fff 100%);
  min-height: 100%;
}

.th-page__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.th-page__title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 600;
  color: #0f172a;
}

.th-page__subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.th-page__count {
  text-align: right;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 12px;
}

.th-page__count-num {
  display: block;
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
}

.th-page__count-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
}

.th-page__toolbar { margin-bottom: 14px; }

.th-page__search {
  display: flex;
  gap: 8px;
  align-items: center;
}

.th-page__search .el-input { flex: 1; }

.th-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.th-card {
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid #e2e8f0 !important;
  position: relative;
}

.th-card__accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #94a3b8;
}

.th-card__accent--success { background: #10b981; }
.th-card__accent--danger { background: #ef4444; }
.th-card__accent--warning { background: #f59e0b; }
.th-card__accent--muted { background: #94a3b8; }

.th-card__body { padding: 14px 14px 12px 18px; }

.th-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.th-card__code {
  font-size: 1.05rem;
  font-weight: 600;
  color: #0f172a;
  word-break: break-all;
}

.th-card__meta { cursor: pointer; }

.th-card__date {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.th-card__dl { margin: 0; }

.th-card__row {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 4px 10px;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.th-card__row:last-child { border-bottom: none; }
.th-card__row dt { margin: 0; color: #94a3b8; }
.th-card__row dd { margin: 0; color: #334155; word-break: break-word; }

.th-card__phone {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.th-card__footer { margin-top: 12px; }

.th-card__btn {
  width: 100%;
  border-radius: 10px !important;
}

.th-page__scroll-status {
  margin-top: 14px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
  padding: 10px 8px 4px;
}

.th-page__loading-more {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #3b82f6;
}

.th-page__end,
.th-page__hint { color: #94a3b8; font-size: 12px; }

.th-page__sentinel {
  height: 1px;
  width: 100%;
}

.th-scroll-loader {
  position: fixed;
  left: 50%;
  bottom: 88px;
  transform: translateX(-50%);
  z-index: 2000;
  pointer-events: none;
}

.th-scroll-loader__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.92);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.28);
  white-space: nowrap;
}

.th-fade-enter-active,
.th-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.th-fade-enter,
.th-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

.th-page__empty { padding: 40px 0; }
</style>
